import { useState, useRef, useCallback } from 'react';
import { api } from '../services/api';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseDraftSaveReturn {
  saveField: (section: string, fieldKey: string, value: string) => void;
  flushPendingSave: () => Promise<void>;
  saveStatus: SaveStatus;
  dataRef: React.MutableRefObject<Record<string, Record<string, string>>>;
}

/**
 * Manages debounced autosave for application form fields.
 *
 * Each section+fieldKey pair has its own debounce timer so that
 * navigating between steps does NOT cancel a pending save for a
 * different field.  flushPendingSave() fires all queued saves
 * immediately (used before submit and on step navigation).
 */
export function useDraftSave(): UseDraftSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // Per-field timers: key = `${section}:${fieldKey}`
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Per-field pending payloads
  const pendingRef = useRef<Record<string, { section: string; fieldKey: string; value: string }>>({});
  const dataRef    = useRef<Record<string, Record<string, string>>>({});

  const saveField = useCallback((section: string, fieldKey: string, value: string) => {
    // Always update the in-memory mirror synchronously
    if (!dataRef.current[section]) dataRef.current[section] = {};
    dataRef.current[section][fieldKey] = value;

    const key = `${section}:${fieldKey}`;
    pendingRef.current[key] = { section, fieldKey, value };

    // Cancel only the timer for THIS field, not others
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    setSaveStatus('saving');

    saveTimers.current[key] = setTimeout(async () => {
      delete saveTimers.current[key];
      const payload = pendingRef.current[key];
      if (!payload) return;
      delete pendingRef.current[key];
      try {
        await api.applicationForm.saveField(payload);
        if (Object.keys(pendingRef.current).length === 0) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        }
      } catch {
        setSaveStatus('error');
      }
    }, 1500);
  }, []);

  // Flush all pending saves immediately (before submit or step navigation)
  const flushPendingSave = useCallback(async () => {
    // Cancel all running timers
    for (const key of Object.keys(saveTimers.current)) {
      clearTimeout(saveTimers.current[key]);
    }
    saveTimers.current = {};

    const entries = Object.values(pendingRef.current);
    pendingRef.current = {};
    if (entries.length === 0) return;

    try {
      await Promise.all(entries.map((e) => api.applicationForm.saveField(e)));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
    }
  }, []);

  return { saveField, flushPendingSave, saveStatus, dataRef };
}
